import useRequest from "@/lib/request/useRequest";
import { useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalContent,
    ModalBody,
    ModalFooter,
    Button,
    Tabs,
    Tab,
    Select,
    SelectItem,
    Breadcrumbs,
    BreadcrumbItem,
    Chip,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTableList,
    faCloudSunRain,
    faArrowRight,
    faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
    PlantingPlan,
    ApplicationPlan,
    SimulatedCropPlan,
} from "@/types/cropPlan";
import { Organization } from "@/types/organization";
import { CropConfiguration } from "@/types/cropConfiguration";
import { NitrogenSimulationType } from "@/types/enums";
import { FieldConfig } from "@/types/fieldConfig";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import dayjs from "dayjs";
import { PlantingPlanForm } from "./PlantingPlanForm";
import { ApplicationPlanForm } from "./ApplicationPlanTable";
import ScenarioPlannerIcon from "@/components/nav/ScenarioPlannerIcon";
import WeatherView from "./WeatherView";
import PlanComparison from "./PlanComparison";
import PlantingMetadata from "./PlantingMetadata";
import { ProcessingStatus } from "@/types/fieldNitrogenPlan";

function FieldPlanner({
    plannerIsOpen,
    onClose,
    field,
}: {
    plannerIsOpen: boolean;
    onClose: () => void;
    field: FieldConfig;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: `/crops/`,
        enabled: plannerIsOpen,
    });

    const organizationsQuery = useGetRequest<Organization[]>({
        url: `/organizations/`,
        enabled: plannerIsOpen,
    });

    const organizationOptions = useMemo(() => {
        if (!organizationsQuery.data) {
            return [];
        }
        return organizationsQuery.data.map((org) => ({
            description: org.name,
            id: org.id,
        }));
    }, [organizationsQuery.data]);

    const [pollForResults, setPollForResults] = useState(false);

    const cropPlansRequest = useGetRequest<SimulatedCropPlan[]>({
        url: `/simulated_crop_plans/`,
        params: {
            field: field.id,
        },
        enabled: plannerIsOpen && !!field.id,
        queryOptions: {
            refetchInterval: pollForResults ? 4000 : undefined,
        },
    });

    const plantingPlansRequest = useGetRequest<PlantingPlan[]>({
        url: `/planting_plans/`,
        enabled: plannerIsOpen,
    });

    const applicationPlansRequest = useGetRequest<ApplicationPlan[]>({
        url: `/application_plans/`,
        enabled: plannerIsOpen,
    });

    const [selectedPlantingPlanId, setSelectedPlantingPlanId] = useState<
        number | null
    >(null);

    const plantingPlan = useMemo<PlantingPlan | null>(() => {
        return (
            plantingPlansRequest.data?.find(
                (plan) => plan.id === selectedPlantingPlanId,
            ) || null
        );
    }, [plantingPlansRequest.data, selectedPlantingPlanId]);

    const selectedCropConfiguration = useMemo(() => {
        return cropsQuery.data?.find(
            (crop) => crop.id === plantingPlan?.crop_configuration,
        );
    }, [cropsQuery.data, plantingPlan?.crop_configuration]);

    const cropStageOptions = useMemo(() => {
        if (!selectedCropConfiguration) {
            return [];
        }
        return selectedCropConfiguration.stages;
    }, [selectedCropConfiguration]);

    const [selectedApplicationPlanIds, setSelectedApplicationPlanIds] =
        useState<number[]>([]);

    const [simulationType, setSimulationType] =
        useState<NitrogenSimulationType>(NitrogenSimulationType.Sentinel);

    useEffect(() => {
        if (!cropPlansRequest?.data) return;
        const anyPlansProcessing = cropPlansRequest.data.some(
            (plan) =>
                plan.processing_status === ProcessingStatus.PROCESSING ||
                plan.processing_status === ProcessingStatus.PENDING,
        );
        setPollForResults(anyPlansProcessing);
    }, [cropPlansRequest?.data]);

    const simulateCropPlansMutation = useMutateRequest<SimulatedCropPlan[]>({
        url: `/simulated_crop_plans/bulk_upsert/`,
        method: "put",

        onSuccess: () => {
            setPollForResults(true);
            cropPlansRequest.refetch();
            plantingPlansRequest.refetch();
            applicationPlansRequest.refetch();
        },
    });

    const applicationPlansLookup = useMemo(() => {
        return new Map(
            (applicationPlansRequest.data ?? []).map((plan) => [
                plan.id != null ? plan.id.toString() : "null",
                plan,
            ]),
        );
    }, [applicationPlansRequest.data]);

    const cropPlans: SimulatedCropPlan[] = useMemo(() => {
        const selectedPlans = Array.from(selectedApplicationPlanIds).map(
            (key) => applicationPlansLookup.get(key.toString()),
        );

        if (!plantingPlan || selectedPlans.length === 0) {
            return [];
        }
        return selectedPlans.map((applicationPlan) => {
            const cropPlan = cropPlansRequest.data?.find(
                (plan) =>
                    plan.application_plan?.id === applicationPlan?.id &&
                    plan.planting_plan?.id === plantingPlan.id &&
                    plan.simulation_type === simulationType,
            );
            return {
                ...(cropPlan ? cropPlan : {}),
                id: cropPlan?.id || null,
                is_primary: cropPlan ? cropPlan.is_primary : false,
                field: field.id,
                planting_plan: plantingPlan && {
                    ...plantingPlan,
                    date: dayjs(plantingPlan.date).format("YYYY-MM-DD"),
                },
                application_plan: applicationPlan || null,
                simulation_type: simulationType as NitrogenSimulationType,
            };
        });
    }, [
        simulationType,
        selectedApplicationPlanIds,
        plantingPlan,
        applicationPlansLookup,
        cropPlansRequest.data,
        field.id,
    ]);

    const primaryCropPlan = useMemo(() => {
        if (!cropPlansRequest.data) {
            return undefined;
        }
        return cropPlansRequest.data.find((plan) => plan.is_primary);
    }, [cropPlansRequest.data]);

    const simulationOptions = [
        {
            key: NitrogenSimulationType.Sentinel,
            label: "Forecast",
        },
        {
            key: NitrogenSimulationType.Drought,
            label: "Drought",
        },
        {
            key: NitrogenSimulationType.Wet,
            label: "Wet",
        },
        {
            key: NitrogenSimulationType.Decadal90,
            label: "Decadal 90",
        },
        {
            key: NitrogenSimulationType.Decadal50,
            label: "Decadal 50",
        },
    ];

    const [plannerStep, setPlannerStep] = useState<
        "planting" | "applications" | "results"
    >("planting");

    return (
        <Modal
            isOpen={plannerIsOpen}
            onClose={onClose}
            classNames={{
                base: "max-w-screen max-h-screen h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] ",
            }}
            isDismissable={false}
        >
            <ModalContent className="m-0 p-0" style={{ margin: 0, padding: 0 }}>
                <ModalHeader className="flex flex-col mb-0 pb-0">
                    <div className="flex flex-row w-full items-center gap-4 font-bold text-3xl text-primary-500">
                        <img
                            src={allNHorizontalImage}
                            alt="All N Horizontal"
                            className="h-8 object-cover"
                        />
                        Planner
                    </div>
                </ModalHeader>
                <ModalBody className="py-0 gap-0 overflow-y-scroll">
                    <Tabs
                        size="lg"
                        color="primary"
                        variant="underlined"
                        fullWidth
                        classNames={{
                            tabContent: "p-0",
                            panel: "py-0",
                        }}
                    >
                        <Tab
                            key="plans"
                            title={
                                <div>
                                    <FontAwesomeIcon icon={faTableList} /> Plans
                                </div>
                            }
                        >
                            <div className="flex flex-row items-center justify-between">
                                <Breadcrumbs
                                    underline="always"
                                    onAction={(key) =>
                                        setPlannerStep(
                                            key as
                                                | "planting"
                                                | "applications"
                                                | "results",
                                        )
                                    }
                                    color="primary"
                                    size="lg"
                                >
                                    <BreadcrumbItem
                                        key="planting"
                                        isCurrent={plannerStep === "planting"}
                                    >
                                        Planting
                                    </BreadcrumbItem>
                                    {(plannerStep == "applications" ||
                                        plannerStep == "results") && (
                                        <BreadcrumbItem
                                            key="applications"
                                            isCurrent={
                                                plannerStep === "applications"
                                            }
                                        >
                                            Applications
                                        </BreadcrumbItem>
                                    )}
                                    {plannerStep == "results" && (
                                        <BreadcrumbItem
                                            key="results"
                                            isCurrent={
                                                plannerStep === "results"
                                            }
                                            isDisabled={
                                                selectedApplicationPlanIds.length ===
                                                0
                                            }
                                        >
                                            Results
                                        </BreadcrumbItem>
                                    )}
                                </Breadcrumbs>
                                {plantingPlan && (
                                    <PlantingMetadata
                                        plantingPlan={plantingPlan}
                                        selectedCropConfiguration={
                                            selectedCropConfiguration
                                        }
                                    />
                                )}
                            </div>

                            {plannerStep === "results" && (
                                <PlanComparison
                                    cropPlans={cropPlans}
                                    cropStageOptions={cropStageOptions}
                                />
                            )}
                            {plannerStep === "planting" && (
                                <PlantingPlanForm
                                    selectedPlantingPlanId={
                                        selectedPlantingPlanId
                                    }
                                    organizations={organizationOptions}
                                    activePlantingOperationId={
                                        field.active_planting_operation.id ===
                                        -1
                                            ? undefined
                                            : field.active_planting_operation.id
                                    }
                                    setSelectedPlantingPlanId={
                                        setSelectedPlantingPlanId
                                    }
                                    plantingPlansRequest={plantingPlansRequest}
                                    cropsQuery={cropsQuery}
                                    primaryCropPlan={primaryCropPlan}
                                />
                            )}
                            {plannerStep === "applications" && (
                                <ApplicationPlanForm
                                    fields={[
                                        {
                                            id: field.id,
                                            active_planting_operation:
                                                field.active_planting_operation
                                                    .id === -1
                                                    ? null
                                                    : {
                                                          id: field
                                                              .active_planting_operation
                                                              .id,
                                                      },
                                        },
                                    ]}
                                    organizations={organizationOptions}
                                    selectedApplicationPlanIds={
                                        selectedApplicationPlanIds
                                    }
                                    setSelectedApplicationPlanIds={
                                        setSelectedApplicationPlanIds
                                    }
                                    applicationPlansRequest={
                                        applicationPlansRequest
                                    }
                                    cropStageOptions={cropStageOptions}
                                    plantingPlan={plantingPlan!}
                                    cropPlansRequest={cropPlansRequest}
                                    simulationType={simulationType}
                                    onClose={onClose}
                                    simulateCropPlansMutation={
                                        simulateCropPlansMutation
                                    }
                                    primaryCropPlan={primaryCropPlan}
                                />
                            )}
                        </Tab>
                        <Tab
                            key="weather"
                            title={
                                <div>
                                    <FontAwesomeIcon icon={faCloudSunRain} />{" "}
                                    Weather
                                </div>
                            }
                        >
                            <WeatherView field={field} />
                        </Tab>
                    </Tabs>
                </ModalBody>
                <ModalFooter className="mr-6 flex flex-row ">
                    <div className="flex flex-row w-full gap-3 justify-between items-center">
                        <div className="flex flex-row items-center gap-2">
                            Field:{" "}
                            <Chip color="primary" variant="flat" radius="md">
                                {field.name}
                            </Chip>
                        </div>

                        <div className="flex flex-row w-full gap-3 justify-end items-center">
                            {plannerStep !== "planting" && (
                                <Select
                                    className="max-w-xs"
                                    size="sm"
                                    items={simulationOptions}
                                    label="Simulation Type"
                                    selectionMode="single"
                                    selectedKeys={new Set([simulationType])}
                                    onSelectionChange={(value) => {
                                        setSimulationType(
                                            value.currentKey as NitrogenSimulationType,
                                        );
                                    }}
                                >
                                    {(item) => (
                                        <SelectItem key={item.key}>
                                            {item.label}
                                        </SelectItem>
                                    )}
                                </Select>
                            )}

                            {plannerStep === "planting" && (
                                <Button
                                    color="primary"
                                    isDisabled={selectedPlantingPlanId === null}
                                    onPress={() => {
                                        setPlannerStep("applications");
                                    }}
                                    startContent={
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    }
                                >
                                    Setup Applications
                                </Button>
                            )}
                            {plannerStep === "applications" && (
                                <div className="flex flex-row gap-2">
                                    <Button
                                        onPress={() => {
                                            setPlannerStep("planting");
                                        }}
                                        startContent={
                                            <FontAwesomeIcon
                                                icon={faArrowLeft}
                                            />
                                        }
                                    >
                                        Back to planting
                                    </Button>

                                    <Button
                                        color="primary"
                                        isDisabled={
                                            selectedPlantingPlanId === null ||
                                            selectedApplicationPlanIds.length ===
                                                0
                                        }
                                        isLoading={
                                            simulateCropPlansMutation.isPending
                                        }
                                        onPress={() => {
                                            simulateCropPlansMutation.mutate(
                                                {
                                                    url: "/simulated_crop_plans/bulk_upsert/",
                                                    method: "put",
                                                    data: cropPlans.map(
                                                        (plan) => ({
                                                            ...plan,
                                                            planting_plan:
                                                                plantingPlan?.id,
                                                            application_plan:
                                                                plan
                                                                    .application_plan
                                                                    ?.id,
                                                        }),
                                                    ),
                                                },
                                                {
                                                    onSuccess: () => {
                                                        setPlannerStep(
                                                            "results",
                                                        );
                                                    },
                                                },
                                            );
                                        }}
                                    >
                                        <ScenarioPlannerIcon color="white" />
                                        {`Run Comparison for ${
                                            Array.from(
                                                selectedApplicationPlanIds,
                                            ).length
                                        } Plans`}
                                    </Button>
                                </div>
                            )}

                            {plannerStep === "results" && (
                                <div className="flex flex-row gap-2">
                                    <Button
                                        color="default"
                                        onPress={() => {
                                            setPlannerStep("applications");
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        Back to applications
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default FieldPlanner;
